import type { z } from 'zod';

import { queryKnowledgeGraphParamsSchema } from '#plugin-chat/lib/data-pipeline/steps/query-knowledge-graph.ts';
import { outputDatasetSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import type { DatasetProducerResult } from '#plugin-chat/lib/tool/output-types.ts';

/**
 * LLM-facing input parameters for {@link QueryKnowledgeGraphTool}.
 *
 * Derived from the step's canonical {@link queryKnowledgeGraphParamsSchema} so
 * the shape of `viewExternalId` / `properties` / `filters` / `sort` / `limit`
 * stays in sync with the step's execute contract. The tool drops `title`
 * (not LLM-facing) and adds `outputDataset` so the execute body can stage the
 * produced dataset into the chat-history store.
 *
 * Uses the canonical {@link rootFilterSchema} filter AST directly. Ref-typed
 * CDF columns (wellString, well, field) are filtered exclusively via
 * `sourceRef` projecting from a previously staged dataset's `$instanceId` —
 * literal `{space, externalId}` values are not part of this schema, since
 * spaces vary by view/tenant and are not safe to guess.
 */
export const QueryKnowledgeGraphParametersSchema = queryKnowledgeGraphParamsSchema.omit({ title: true }).extend({
  outputDataset: outputDatasetSchema,
});

/** @public */
export type QueryKnowledgeGraphParameters = z.infer<typeof QueryKnowledgeGraphParametersSchema>;

/**
 * @public Output type from the query knowledge graph tool.
 *
 * Aliases the shared {@link DatasetProducerResult} contract —
 * QueryKnowledgeGraphTool stores the produced dataset envelope without adding
 * tool-specific fields.
 */
export type QueryKnowledgeGraphOutput = DatasetProducerResult;
